/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, May 4th 2022, 2:44:07 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import Grid from "@mui/material/Grid";
import { WorkProfile, WorkerAccount } from "lib/schemas";
import { EditToggler, APIItemDisplayWrapper } from "ui-widgets";
import { AutoSaveAPIForm, TextFormInput } from "ui-widgets/forms";
import { useWorkProfileAPIForm } from "workers/services/WorkersAPI";

interface WorkProfileEditProps {
  worker: WorkerAccount;
  editModeCondition: boolean;
}

export const WorkProfileEdit = ({ worker, editModeCondition }: WorkProfileEditProps) => {
  const {
    useWorkProfileAPIFormReturn,
    workProfile,
    workProfileFetchState: { isMakingAPIRequest, httpResponseStatusCode, isAPIError },
  } = useWorkProfileAPIForm(worker?.id);

  const {
    useFormReturn: {
      formState: { errors },
    },
    apiDataSendErrors,
  } = useWorkProfileAPIFormReturn;

  const fieldHasError = (fieldName: keyof WorkProfile) => fieldName in errors || fieldName in apiDataSendErrors;

  return (
    <APIItemDisplayWrapper
      showLoader={isMakingAPIRequest}
      httpResponseStatusCode={httpResponseStatusCode}
      isAPIError={isAPIError}
    >
      <AutoSaveAPIForm
        showSubmitButton={false}
        showFeedbackAlerts={false}
        useAPIFormReturn={useWorkProfileAPIFormReturn}
        sx={{}}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <div className="os-profile-field-label">Job Title</div>
          </Grid>
          <Grid item xs={12} md={6}>
            <EditToggler
              editModeCondition={editModeCondition}
              hasError={fieldHasError("job_title")}
              renderEditComponent={({ toggleEditMode, setValueInputRef }) => (
                <TextFormInput<WorkProfile> inputRef={setValueInputRef} schema_field_key="job_title" />
              )}
            >
              {({ editIcon }) => (
                <div className="os-profile-field-value">
                  {workProfile?.job_title} {editIcon}
                </div>
              )}
            </EditToggler>
          </Grid>

          <Grid item xs={12} md={6}>
            <div className="os-profile-field-label">Work ID</div>
          </Grid>
          <Grid item xs={12} md={6}>
            <EditToggler
              editModeCondition={editModeCondition}
              hasError={fieldHasError("work_identification_number")}
              renderEditComponent={({ toggleEditMode, setValueInputRef }) => (
                <TextFormInput<WorkProfile> inputRef={setValueInputRef} schema_field_key="work_identification_number" />
              )}
            >
              {({ editIcon }) => (
                <div className="os-profile-field-value">
                  {workProfile?.work_identification_number} {editIcon}
                </div>
              )}
            </EditToggler>
          </Grid>

          <Grid item xs={12} md={6}>
            <div className="os-profile-field-label">Work Email</div>
          </Grid>
          <Grid item xs={12} md={6}>
            <EditToggler
              editModeCondition={editModeCondition}
              hasError={fieldHasError("work_email")}
              renderEditComponent={({ toggleEditMode, setValueInputRef }) => (
                <TextFormInput<WorkProfile> inputRef={setValueInputRef} schema_field_key="work_email" />
              )}
            >
              {({ editIcon }) => (
                <div className="os-profile-field-value">
                  {workProfile?.work_email} {editIcon}
                </div>
              )}
            </EditToggler>
          </Grid>

          <Grid item xs={12} md={6}>
            <div className="os-profile-field-label">Work Phone</div>
          </Grid>
          <Grid item xs={12} md={6}>
            <EditToggler
              editModeCondition={editModeCondition}
              hasError={fieldHasError("work_phone")}
              renderEditComponent={({ toggleEditMode, setValueInputRef }) => (
                <TextFormInput<WorkProfile> inputRef={setValueInputRef} schema_field_key="work_phone" />
              )}
            >
              {({ editIcon }) => (
                <div className="os-profile-field-value">
                  {workProfile?.work_phone} {editIcon}
                </div>
              )}
            </EditToggler>
          </Grid>
        </Grid>
      </AutoSaveAPIForm>
    </APIItemDisplayWrapper>
  );
};
